import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/baseQuery';
import { METHODS } from 'config';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  job: string;
}

interface Announcement {
  _id: string;
  title: string;
  content: string;
  creator: User;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AnnouncementListQuery {
  page?: number;
  limit?: number;
}

interface AnnouncementListResponse {
  data: Announcement[];
  count: number;
}

interface CreateAnnouncementInput {
  title: string;
  content: string;
  category: 'administrative' | 'technical' | 'social' | 'notification' | 'event';
}

const AnnouncementApi = createApi({
  reducerPath: 'announcement-api',
  baseQuery: baseQuery(),
  tagTypes: ['Announcements'],
  endpoints: (builder) => ({
    getAnnouncements: builder.query<AnnouncementListResponse, AnnouncementListQuery>({
      query: (params) => ({
        url: '/announcement/list',
        method: METHODS.get,
        params,
      }),
      providesTags: ['Announcements'],
    }),
    getAnnouncement: builder.query<{ announcement: Announcement }, string>({
      query: (announcementId) => ({
        url: `/announcement/get`,
        method: METHODS.get,
        params: { announcementId },
      }),
      providesTags: ['Announcements'],
    }),
    createAnnouncement: builder.mutation<{ announcement: Announcement }, CreateAnnouncementInput>({
      query: (body) => ({
        url: '/announcement/create',
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Announcements'],
    }),
    pinAnnouncement: builder.mutation<{ announcement: Announcement }, { announcementId: string; isPinned: boolean }>({
      query: (body) => ({
        url: `/announcement/pin`,
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Announcements'],
    }),
    deleteAnnouncement: builder.mutation<{ success: boolean }, string>({
      query: (announcementId) => ({
        url: `/announcement/remove`,
        method: METHODS.post,
        params: { announcementId },
      }),
      invalidatesTags: ['Announcements'],
    }),
  }),
});

export const {
  useGetAnnouncementsQuery,
  useGetAnnouncementQuery,
  useCreateAnnouncementMutation,
  usePinAnnouncementMutation,
  useDeleteAnnouncementMutation,
} = AnnouncementApi;

export { AnnouncementApi };
export type { Announcement, AnnouncementListQuery, CreateAnnouncementInput };
